import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import logo from "../../assets/images/ai-worker-removebg.png";
import { sendOtp } from "../../services/oprations/authAPI";
import { setSigninData } from "../../slices/authSlice";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(value && !validateEmail(value) ? "Please enter a valid email address" : "");
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
  };

  const handleSendOtp = (e) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");

    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!password) {
      setPasswordError("Password is required");
      return;
    }

    dispatch(setSigninData({ email, password }));
    dispatch(sendOtp(email, password, navigate));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Logo" width={180} height={40} className="object-contain cursor-pointer" />
        </div>

        {/* Card */}
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
          <h1 className="text-3xl font-bold text-slate-800 mb-2 text-center">Welcome back</h1>
          <p className="text-slate-500 mb-8 text-center">
            Don’t have an account?{" "}
            <button
              onClick={() => navigate("/signup")}
              className="font-semibold text-cyan-500 hover:underline"
            >
              Create account
            </button>
          </p>

          <form onSubmit={handleSendOtp} className="space-y-6">
            {/* Email */}
            <div>
              <label className="text-sm font-medium text-slate-600">Enter your email</label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="username@neurospace.pro"
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg transition-all duration-300 outline-none ${
                    emailError ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-100"
                  } focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400`}
                />
              </div>
              {emailError && (
                <div className="flex items-center gap-2 text-sm text-red-500 mt-1 animate-pulse">
                  <AlertCircle size={14} /> {emailError}
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="text-sm font-medium text-slate-600">Enter your password</label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="••••••••"
                  className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg transition-all duration-300 outline-none ${
                    passwordError ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-100"
                  } focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {passwordError && (
                <div className="flex items-center gap-2 text-sm text-red-500 mt-1 animate-pulse">
                  <AlertCircle size={14} /> {passwordError}
                </div>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <a href="/forget-password" className="text-sm font-semibold text-cyan-500 hover:underline">
                Forgot password?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-3 px-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Send OTP
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
